import React from 'react';
import { useModalStack } from './ModalStackProvider';
import type { ModalComponent, ModalKey } from './modalRegistry';

/**
 * Props for {@link ModalHost}.
 *
 * @property registry
 * A mapping of modal keys to modal components.
 * This is typically the global modal registry for the application.
 */
type ModalHostProps = {
  registry: Record<ModalKey, ModalComponent<any>>;
};

/**
 * ModalHost
 *
 * Architectural role:
 * - Acts as the **single rendering point** for all modal workflows.
 * - Renders the top-most modal from the modal stack.
 * - Injects modal host controls (`open`, `close`, `resolve`, `reject`)
 *   into the modal component.
 *
 * Important constraints:
 * - Modal components rendered here MUST come from the modal registry.
 * - Modal components should NEVER be rendered directly elsewhere.
 *
 * Data flow:
 * - Callers push modal requests onto the stack via `open(key, props)`
 * - ModalHost renders the active modal
 * - Modal resolves/rejects, and ModalHost delegates that back to the stack
 */
export const ModalHost = ({ registry }: ModalHostProps) => {
  /**
   * Modal stack state and host-level control functions.
   */
  const { stack, open, closeTop, resolveTop, rejectTop } = useModalStack();

  /**
   * Determine the top-most modal on the stack.
   * Only one modal is rendered at a time.
   */
  const top = stack[stack.length - 1];
  if (!top) return null;

  /**
   * Resolve the modal component from the registry using its key.
   *
   * The cast is safe because:
   * - All keys pushed onto the stack must exist in the registry
   * - ModalComponent typing is enforced at the registry boundary
   */
  const ModalComponent = registry[top.key] as any;

  return (
    <ModalComponent
      /**
       * Props originally passed to `open(key, props)`.
       */
      {...top.props}
      /**
       * Expose modal stack controls to the modal component.
       *
       * These form the modal's "host contract" and allow it to:
       * - open additional modals
       * - close itself
       * - resolve or reject its promise
       */
      open={open}
      close={() => closeTop()}
      resolve={(result: any) => resolveTop(result)}
      reject={(reason?: unknown) => rejectTop(reason)}
    />
  );
};
